/* eslint-disable @typescript-eslint/no-explicit-any */
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Permission } from "@iventis/domain-model/model/permission";
import { PermissionResourceType } from "@iventis/domain-model/model/permissionResourceType";
import { sectionalMargin } from "@iventis/styles/src/atomic-rules";
import { Content } from "@iventis/translations/content/typed-content";
import React, { FunctionComponent } from "react";
import { styled } from "@iventis/styles";
import { BaseActionObject, ResolveTypegenMeta, ServiceMap, State, TypegenDisabled } from "xstate";
import { Header5 } from "@iventis/styles/src/components/texts";
import { Node } from "@iventis/domain-model/model/node";
import { LoadingComponent } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { isAdmin } from "@iventis/utilities";
import { PermissionsResourceComponent } from "./permissions-resource";
import { PermissionContext, PermissionEvents } from "./permissions-edit.machine";

export const PermissionsEditComponent: React.FC<{
    close?: () => void;
    state: State<
        PermissionContext,
        PermissionEvents,
        any,
        {
            value: any;
            context: PermissionContext;
        },
        ResolveTypegenMeta<TypegenDisabled, PermissionEvents, BaseActionObject, ServiceMap>
    >;
    send: (event: PermissionEvents | PermissionEvents[]) => void;
    includeHeader?: boolean;
}> = ({ state, send, includeHeader = true }) => {
    const translate = useIventisTranslate();
    const { latestPermissions, permissionSelector, node, canLock } = state.context;
    const loading = state.matches("loading");
    const getPermissionsFailed = state.matches("getPermissionsFailed");
    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: "stretch",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: sectionalMargin,
                    height: "100%",
                    minWidth: "600px",
                }}
                data-testid="permission-edit-form"
            >
                {includeHeader && <PermissionsEditHeader node={node} />}
                {loading && <LoadingComponent />}
                {getPermissionsFailed && <div>{translate(Content.common.errorLoading, { resourceName: translate(Content.permissions.permissions) })}</div>}
                {!loading && !getPermissionsFailed && (
                    <div className="permissionsContainer">
                        <StyledPermissionsResourceComponent
                            permissions={latestPermissions}
                            title={node.name}
                            resourceId={node.sourceId} // We must use the source id when chaning permissions for linked resources. Linked resources have a differing sourceId and id.
                            disabled={!isAdmin(node.authorisation)}
                            resourceType={(node.type as string) as PermissionResourceType}
                            openPermissionSelectorId={permissionSelector}
                            canLock={canLock}
                            addPermission={(permission: Permission) => send({ type: "ADD_PERMISSION", payload: permission })}
                            removePermission={(permission: Permission) => send({ type: "REMOVE_PERMISSION", payload: permission })}
                            changePermission={(permission: Permission, authorisation: Authorisation) =>
                                send({ type: "CHANGE_PERMISSION", payload: { changingPermission: permission, authorisation } })
                            }
                            loading={state.matches("breakInheritance") || state.matches("restoreInheritance")}
                            changeInheritance={(resourceId: string, inheritance: boolean) =>
                                inheritance ? send({ type: "RESTORE_INHERITANCE" }) : send({ type: "BREAK_INHERITANCE" })
                            }
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export const PermissionsEditHeader: FunctionComponent<{ node: Node }> = ({ node }) => {
    const translate = useIventisTranslate();
    return (
        <Header5>
            {translate(Content.permissions.permissionsEditTitle, {
                nodeType: translate(Content.library.node_names[node.type]).toLocaleLowerCase(),
            })}
        </Header5>
    );
};

const StyledPermissionsResourceComponent = styled(PermissionsResourceComponent)`
    overflow-y: auto;
    flex-grow: 1;
`;
